import { motion } from 'framer-motion';
import { Video, Calendar, Clock, Users, ExternalLink, MessageCircle, MapPin, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

const platformLinks = [
  { name: 'Facebook', url: 'https://facebook.com', color: '#1877F2' },
  { name: 'YouTube', url: 'https://youtube.com', color: '#FF0000' },
  { name: 'Instagram', url: 'https://instagram.com', color: '#E4405F' },
  { name: 'TikTok', url: 'https://tiktok.com', color: '#000000', badge: 'Coming Soon' },
];

export const Services = () => {
  return (
    <div data-testid="services-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold uppercase tracking-[0.3em] text-sm mb-4">Join Us</p>
            <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Virtual & In-Person <span className="text-gold">Services</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Join us for virtual or in-person services. Whether you're local to Houston or connecting from anywhere in the world, there's a place for you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Service Bento Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Service Card - Large */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 lg:row-span-2 card-dark gold-glow relative overflow-hidden"
              data-testid="main-service-card"
            >
              <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
                <img
                  src="https://customer-assets.emergentagent.com/job_faith-revamp-1/artifacts/n6h10t5c_Lion%20King%202.png"
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm mb-6">
                  <Video size={16} />
                  <span>Sabbath Worship Service</span>
                </div>
                <h2 className="font-cinzel text-3xl sm:text-4xl text-foreground mb-4">
                  Sabbath Service
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Join us every Sabbath (Saturday) for our main worship service. Attend in person at our Houston location or join online via YouTube — we live stream our service so you can worship with us from anywhere.
                </p>
                
                <div className="flex flex-wrap gap-6 mb-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="text-gold" size={24} />
                    <div>
                      <p className="text-foreground font-semibold">Every Saturday</p>
                      <p className="text-muted-foreground text-sm">Sabbath Day</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="text-gold" size={24} />
                    <div>
                      <p className="text-foreground font-semibold">9:45 AM CST</p>
                      <p className="text-muted-foreground text-sm">Duration: ~2 hours</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 mb-6 p-3 bg-muted/30 rounded-sm">
                  <MapPin className="text-gold flex-shrink-0 mt-0.5" size={20} />
                  <div className="text-sm">
                    <p className="text-foreground font-semibold">In-Person Location</p>
                    <p className="text-muted-foreground">10303 Northwest Freeway, Suite 263, Houston, TX 77092</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <a
                    href="https://www.youtube.com/channel/UCdJtc_ATtcQV6BKByvucFQA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="join-youtube-btn"
                  >
                    Watch on YouTube
                    <ExternalLink size={18} />
                  </a>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=10303+Northwest+Freeway+Suite+263+Houston+TX+77092"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary inline-flex items-center gap-2"
                  >
                    Get Directions
                    <MapPin size={18} />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Wednesday Prayer & B.I.D.D. Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-dark"
              data-testid="bidds-card"
            >
              <div className="w-12 h-12 rounded-full bg-deep-red/20 flex items-center justify-center mb-4">
                <Video className="text-deep-red" size={24} />
              </div>
              <h3 className="font-cinzel text-xl text-foreground mb-2">Prayer & B.I.D.D.</h3>
              <p className="text-muted-foreground text-sm mb-2">
                <strong>Wednesdays @ 7:00 PM CST</strong>
              </p>
              <p className="text-muted-foreground text-sm mb-1">
                Break It Down Discussions — We study the "Lost Books" of the Bible, exploring scripture in its original context.
              </p>
              <p className="text-gold text-xs mb-4">Virtual Service via Zoom</p>
              <a href="https://us06web.zoom.us/j/94303159703?pwd=d1ZoRFRaK0w4V29qOVY2Mjc3dVVqQT09" target="_blank" rel="noopener noreferrer" className="text-gold text-sm uppercase tracking-wider flex items-center gap-2">
                Join on Zoom <ExternalLink size={14} />
              </a>
            </motion.div>

            {/* Prayer Request Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card-dark"
              data-testid="prayer-card"
            >
              <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center mb-4">
                <MessageCircle className="text-gold" size={24} />
              </div>
              <h3 className="font-cinzel text-xl text-foreground mb-3">Prayer Requests</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Share your prayer needs with our community.
              </p>
              <Link to="/contact" className="text-gold text-sm uppercase tracking-wider flex items-center gap-2">
                Submit Request <ExternalLink size={14} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Streaming Platforms */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-cinzel text-3xl text-gold mb-4">Watch Us Live</h2>
            <p className="text-muted-foreground">
              Our services are streamed across multiple platforms. Choose your favorite.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {platformLinks.map((platform, index) => (
              <motion.a
                key={platform.name}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-dark text-center hover:scale-105 transition-transform relative"
                data-testid={`platform-${platform.name.toLowerCase()}`}
              >
                {platform.badge && (
                  <span className="absolute -top-2 -right-2 bg-gold text-black text-xs px-2 py-1 rounded-full">
                    {platform.badge}
                  </span>
                )}
                <div
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{ backgroundColor: `${platform.color}20` }}
                >
                  <ExternalLink size={20} style={{ color: platform.color }} />
                </div>
                <p className="text-foreground font-semibold">{platform.name}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* The Holy WORD Bible App - Study Tool CTA */}
      <section className="py-24 relative overflow-hidden" data-testid="holy-word-app-section">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://customer-assets.emergentagent.com/job_faith-revamp-1/artifacts/1x0fdbpc_Lion%20King%201.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-dark gold-glow text-center"
            data-testid="holy-word-app-card"
          >
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm mb-6">
              <BookOpen size={16} />
              <span>Our Official Study Tool</span>
            </div>
            <h2 className="font-cinzel text-3xl sm:text-4xl text-foreground mb-4">
              The Holy <span className="text-gold">WORD</span> Bible
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Read and study the Bible with our Ministry's official app — available on Google Play, the App Store, and the web.
            </p>
            <Link to="/bible-app" className="btn-primary inline-flex items-center gap-2" data-testid="explore-app-btn">
              Explore the App
              <ExternalLink size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-cinzel text-3xl text-gold mb-4">What to Expect</h2>
          </motion.div>

          <div className="space-y-6">
            {[
              { title: 'Scripture Reading', desc: 'We begin with reading the passages we\'ll be studying, often in multiple translations.' },
              { title: 'Original Language Study', desc: 'We explore the Hebrew and Greek roots of key words to understand their true meaning.' },
              { title: 'Open Discussion', desc: 'Everyone is encouraged to ask questions and share insights. This is a conversation, not a lecture.' },
              { title: 'Practical Application', desc: 'We discuss how to apply these teachings to our daily lives.' },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4 items-start"
              >
                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-gold text-black flex items-center justify-center font-bold">
                  {index + 1}
                </div>
                <div>
                  <h3 className="text-foreground font-semibold mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
